import React, { useState, useEffect } from 'react';
import { Goal } from '../types';
import { CapabilityDecision, CompleteAssessment, CapabilityDomain, CAPABILITY_DOMAIN_LABELS } from './types';
import { createMilestone } from '../firebase/milestones.service';
import { createAction } from '../firebase/actions.service';
import AISparkPanel from '../shared/AISparkPanel';
import { GoalIcon, MilestoneIcon, SuccessIcon, InsightIcon, EditIcon, DeleteIcon, ChatIcon, AssistantIcon } from '../../../icons';

// =============================================================================
// TYPES
// =============================================================================

interface PostAssessmentMilestonesReviewProps {
  goal: Goal;
  ownerId: string;
  assessment: CompleteAssessment;
  onComplete: () => void;
  onBack?: () => void;
}

interface MilestoneCard {
  domain: string;
  domainLabel: string;
  title: string;
  decision: CapabilityDecision;
  actions: Array<{
    title: string;
    enabled: boolean;
    source: string;
  }>;
  startDate: string;
  endDate: string;
  priority: 'high' | 'medium' | 'low';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * PostAssessmentMilestonesReview
 * 
 * Toont mijlpalen die zijn gegenereerd op basis van het volledige assessment
 * Gebruiker kan:
 * - Acties bewerken/toevoegen/verwijderen
 * - AI Spark gebruiken voor hulp
 * - Datums aanpassen
 * - Prioriteit instellen
 * - Bevestigen en opslaan
 */
const PostAssessmentMilestonesReview: React.FC<PostAssessmentMilestonesReviewProps> = ({
  goal,
  ownerId,
  assessment,
  onComplete,
  onBack,
}) => {
  console.log('🟢 [PostAssessmentMilestonesReview] Rendered!', {
    goalId: goal.id,
    assessmentDecisions: assessment.decisions.length
  });
  
  const [milestones, setMilestones] = useState<MilestoneCard[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0); // First card expanded by default
  const [editingActionIndex, setEditingActionIndex] = useState<{ milestone: number; action: number } | null>(null);
  const [editingActionTitle, setEditingActionTitle] = useState('');
  const [addingActionToMilestone, setAddingActionToMilestone] = useState<number | null>(null);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [sparkMilestoneIndex, setSparkMilestoneIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Generate milestones from assessment
  useEffect(() => {
    console.log('📝 [PostAssessmentMilestonesReview] Generating milestones from assessment');
    const generated = generateMilestonesFromAssessment(assessment, goal);
    console.log('✅ [PostAssessmentMilestonesReview] Generated', generated.length, 'milestones');
    setMilestones(generated);
  }, [assessment, goal]);

  // Handlers
  const handleToggleAction = (milestoneIndex: number, actionIndex: number) => {
    setMilestones(prev => prev.map((m, mi) =>
      mi === milestoneIndex
        ? {
            ...m,
            actions: m.actions.map((a, ai) =>
              ai === actionIndex ? { ...a, enabled: !a.enabled } : a
            )
          }
        : m
    ));
  };

  const handleStartEditAction = (milestoneIndex: number, actionIndex: number) => {
    setEditingActionIndex({ milestone: milestoneIndex, action: actionIndex });
    setEditingActionTitle(milestones[milestoneIndex].actions[actionIndex].title);
  };

  const handleSaveEditAction = () => {
    if (editingActionIndex && editingActionTitle.trim()) {
      setMilestones(prev => prev.map((m, mi) =>
        mi === editingActionIndex.milestone
          ? {
              ...m,
              actions: m.actions.map((a, ai) =>
                ai === editingActionIndex.action ? { ...a, title: editingActionTitle.trim() } : a
              )
            }
          : m
      ));
      setEditingActionIndex(null);
      setEditingActionTitle('');
    }
  };

  const handleDeleteAction = (milestoneIndex: number, actionIndex: number) => {
    setMilestones(prev => prev.map((m, mi) =>
      mi === milestoneIndex
        ? { ...m, actions: m.actions.filter((_, ai) => ai !== actionIndex) }
        : m
    ));
  };

  const handleAddAction = (milestoneIndex: number) => {
    if (newActionTitle.trim()) {
      setMilestones(prev => prev.map((m, mi) =>
        mi === milestoneIndex
          ? {
              ...m,
              actions: [...m.actions, {
                title: newActionTitle.trim(),
                enabled: true,
                source: 'user_added'
              }]
            }
          : m
      ));
      setNewActionTitle('');
      setAddingActionToMilestone(null);
    }
  };

  const handleAISparkAddAction = (milestoneIndex: number, actionTitle: string) => {
    setMilestones(prev => prev.map((m, mi) =>
      mi === milestoneIndex
        ? {
            ...m,
            actions: [...m.actions, {
              title: actionTitle,
              enabled: true,
              source: 'ai_spark'
            }]
          }
        : m
    ));
  };

  const handleUpdateDate = (milestoneIndex: number, field: 'startDate' | 'endDate', value: string) => {
    setMilestones(prev => prev.map((m, mi) =>
      mi === milestoneIndex ? { ...m, [field]: value } : m
    ));
  };

  const handleUpdatePriority = (milestoneIndex: number, priority: 'high' | 'medium' | 'low') => {
    setMilestones(prev => prev.map((m, mi) =>
      mi === milestoneIndex ? { ...m, priority } : m
    ));
  };

  const handleSaveAll = async () => {
    setIsSaving(true);

    try {
      for (const milestone of milestones) {
        const enabledActions = milestone.actions.filter(a => a.enabled);
        
        if (enabledActions.length === 0) continue; // Skip milestones with no actions

        // Create milestone
        const milestoneId = await createMilestone(ownerId, goal.id, {
          title: milestone.title,
          targetDate: milestone.endDate,
          category: mapDomainToCategory(milestone.domain),
          origin: 'capability',
          fromSparring: false,
        });

        // Create actions
        for (const action of enabledActions) {
          await createAction(ownerId, goal.id, milestoneId, {
            title: action.title,
            completed: false,
            priority: milestone.priority,
            domain: milestone.domain,
            origin: action.source === 'ai_spark' ? 'sparring' : 
                    action.source === 'user_added' ? 'manual' : 'milestone_generated',
          });
        }
      }

      onComplete();
    } catch (err: any) {
      console.error('Failed to save milestones:', err);
      alert('Er ging iets mis bij het opslaan. Probeer het opnieuw.');
      setIsSaving(false);
    }
  };

  const totalActions = milestones.reduce((sum, m) => sum + m.actions.filter(a => a.enabled).length, 0);
  const milestonesWithActions = milestones.filter(m => m.actions.filter(a => a.enabled).length > 0).length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 rounded-t-lg">
          <div className="flex items-center gap-3 mb-2">
            <GoalIcon size={36} className="text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">
                Assessment Compleet! Nu Acties Plannen
              </h2>
              <p className="text-green-100 text-sm mt-1">
                {goal.title}
              </p>
            </div>
          </div>
          <div className="flex gap-6 text-white mt-4">
            <div className="flex items-center gap-2">
              <MilestoneIcon size={22} className="text-white" />
              <span>{milestones.length} Mijlpalen</span>
            </div>
            <div className="flex items-center gap-2">
              <SuccessIcon size={22} className="text-white" />
              <span>{totalActions} Acties</span>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-start gap-3">
            <InsightIcon size={22} className="text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-800 mb-1">
                Bekijk en pas je actielijsten aan
              </p>
              <p className="text-xs text-gray-600">
                We hebben mijlpalen gemaakt op basis van je assessment. Vink aan welke acties je wilt uitvoeren, 
                bewerk ze, of voeg nieuwe toe. Gebruik de AI Spar knop voor extra hulp!
              </p>
            </div>
          </div>
        </div>

        {/* Milestones List */}
        <div className="p-6 max-h-[60dvh] overflow-y-auto space-y-4">
          {milestones.map((milestone, mIndex) => {
            const isExpanded = expandedIndex === mIndex;
            const enabledCount = milestone.actions.filter(a => a.enabled).length;

            return (
              <div
                key={mIndex}
                className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all"
              >
                {/* Milestone Header */}
                <div
                  className="bg-gradient-to-r from-orange-50 to-pink-50 p-4 cursor-pointer"
                  onClick={() => setExpandedIndex(isExpanded ? null : mIndex)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-2xl">{mIndex + 1}.</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {milestone.title}
                        </h3>
                        <p className="text-xs text-gray-600">
                          {milestone.domainLabel} • {enabledCount} acties geselecteerd
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-4 bg-white space-y-4">
                    {/* Actions List */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Acties ({enabledCount} geselecteerd)
                      </label>
                      <div className="space-y-2">
                        {milestone.actions.map((action, aIndex) => (
                          <div
                            key={aIndex}
                            className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                              action.enabled
                                ? 'bg-orange-50 border-orange-200'
                                : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={action.enabled}
                              onChange={() => handleToggleAction(mIndex, aIndex)}
                              className="mt-1 h-5 w-5 text-orange-500 rounded focus:ring-orange-500"
                            />

                            <div className="flex-1 min-w-0">
                              {editingActionIndex?.milestone === mIndex && editingActionIndex?.action === aIndex ? (
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={editingActionTitle}
                                    onChange={(e) => setEditingActionTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveEditAction();
                                      if (e.key === 'Escape') setEditingActionIndex(null);
                                    }}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                                    autoFocus
                                  />
                                  <button
                                    onClick={handleSaveEditAction}
                                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => setEditingActionIndex(null)}
                                    className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <p className={`text-sm ${action.enabled ? 'text-gray-800' : 'text-gray-500'}`}>
                                    {action.title}
                                  </p>
                                  {action.source === 'ai_spark' && (
                                    <span className="text-xs text-purple-600 flex items-center gap-1"><AssistantIcon size={12} className="text-purple-500" /> AI Spark</span>
                                  )}
                                  {action.source === 'user_added' && (
                                    <span className="text-xs text-blue-600">Door jou toegevoegd</span>
                                  )}
                                </>
                              )}
                            </div>

                            {editingActionIndex?.milestone !== mIndex || editingActionIndex?.action !== aIndex ? (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleStartEditAction(mIndex, aIndex)}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Bewerken"
                                >
                                  <EditIcon size={16} className="text-gray-400 hover:text-blue-600" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAction(mIndex, aIndex)}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  title="Verwijderen"
                                >
                                  <DeleteIcon size={16} className="text-gray-400 hover:text-red-600" />
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>

                      {/* Add Action */}
                      {addingActionToMilestone === mIndex ? (
                        <div className="mt-3 flex gap-2">
                          <input
                            type="text"
                            value={newActionTitle}
                            onChange={(e) => setNewActionTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddAction(mIndex);
                              if (e.key === 'Escape') {
                                setAddingActionToMilestone(null);
                                setNewActionTitle('');
                              }
                            }}
                            placeholder="Nieuwe actie..."
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                            autoFocus
                          />
                          <button
                            onClick={() => handleAddAction(mIndex)}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                          >
                            Toevoegen
                          </button>
                          <button
                            onClick={() => {
                              setAddingActionToMilestone(null);
                              setNewActionTitle('');
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Annuleer
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddingActionToMilestone(mIndex)}
                          className="mt-3 w-full py-2 border-2 border-dashed border-gray-300 rounded-lg
                                   text-gray-500 hover:border-orange-500 hover:text-orange-500 transition-all"
                        >
                          + Nieuwe actie toevoegen
                        </button>
                      )}
                    </div>

                    {/* AI Spark Button */}
                    <button
                      onClick={() => setSparkMilestoneIndex(mIndex)}
                      className="w-full p-3 bg-gradient-to-r from-blue-50 to-purple-50 
                               border border-blue-200 rounded-lg hover:from-blue-100 hover:to-purple-100
                               transition-all flex items-center justify-center gap-2"
                    >
                      <ChatIcon size={20} className="text-blue-600" />
                      <span className="font-semibold text-gray-700">AI Spar: Krijg Tips & Extra Acties</span>
                    </button>

                    {/* Dates & Priority */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Startdatum</label>
                        <input
                          type="date"
                          value={milestone.startDate}
                          onChange={(e) => handleUpdateDate(mIndex, 'startDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Einddatum</label>
                        <input
                          type="date"
                          value={milestone.endDate}
                          onChange={(e) => handleUpdateDate(mIndex, 'endDate', e.target.value)}
                          min={milestone.startDate}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Prioriteit</label>
                        <select
                          value={milestone.priority}
                          onChange={(e) => handleUpdatePriority(mIndex, e.target.value as any)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        >
                          <option value="high">Hoog</option>
                          <option value="medium">Middel</option>
                          <option value="low">Laag</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
          {onBack ? (
            <button
              onClick={onBack}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              ← Terug
            </button>
          ) : (
            <div />
          )}

          <button
            onClick={handleSaveAll}
            disabled={isSaving || milestonesWithActions === 0}
            className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 
                     hover:from-green-600 hover:to-emerald-600 text-white rounded-lg
                     font-bold shadow-lg hover:shadow-xl transition-all
                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                <span>Opslaan...</span>
              </>
            ) : (
              <>
                <span>Bevestigen & Opslaan ({milestonesWithActions} mijlpalen)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* AI Spark Panel */}
      {sparkMilestoneIndex !== null && (
        <AISparkPanel
          milestone={{
            title: milestones[sparkMilestoneIndex].title,
            domain: milestones[sparkMilestoneIndex].domain as any,
            actions: milestones[sparkMilestoneIndex].actions,
          }}
          assessmentContext={{
            type: 'capability',
            decision: milestones[sparkMilestoneIndex].decision,
            goalContext: goal.description,
          }}
          onAddAction={(actionTitle) => handleAISparkAddAction(sparkMilestoneIndex, actionTitle)}
          onClose={() => setSparkMilestoneIndex(null)}
        />
      )}
    </div>
  );
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function generateMilestonesFromAssessment(
  assessment: CompleteAssessment,
  goal: Goal
): MilestoneCard[] {
  const milestones: MilestoneCard[] = [];

  for (const decision of assessment.decisions) {
    // Skip domains that aren't required or don't have a resolution
    if (!decision.required || !decision.resolution) {
      continue;
    }

    const domain: CapabilityDomain = decision.domain;
    const domainLabel = CAPABILITY_DOMAIN_LABELS[domain];
    
    // Generate title based on resolution
    let title = '';
    if (decision.resolution === 'learn') {
      title = `${domainLabel} Basis Leren`;
    } else if (decision.resolution === 'outsource') {
      title = `${domainLabel} Uitbesteden`;
    } else if (decision.resolution === 'hire') {
      title = `${domainLabel} Team Opzetten`;
    } else if (decision.resolution === 'delegate') {
      title = `${domainLabel} Delegeren`;
    } else {
      title = `${domainLabel} Ontwikkelen`;
    }

    // Generate actions based on resolution and assessment level
    const actions: Array<{ title: string; enabled: boolean; source: string }> = [];

    if (decision.resolution === 'learn') {
      actions.push(
        { title: `Volg online cursus over ${domainLabel}`, enabled: true, source: 'generated' },
        { title: `Lees 2-3 boeken over ${domainLabel}`, enabled: true, source: 'generated' },
        { title: `Bekijk tutorials en praktijkvoorbeelden`, enabled: true, source: 'generated' }
      );
    } else if (decision.resolution === 'outsource') {
      actions.push(
        { title: `Vergelijk 3 bureaus/freelancers voor ${domainLabel}`, enabled: true, source: 'generated' },
        { title: `Vraag offertes aan`, enabled: true, source: 'generated' },
        { title: `Check reviews en portfolio's`, enabled: true, source: 'generated' },
        { title: `Plan intake gesprekken met top 2 kandidaten`, enabled: true, source: 'generated' }
      );
    } else if (decision.resolution === 'hire') {
      actions.push(
        { title: `Schrijf vacature voor ${domainLabel} medewerker`, enabled: true, source: 'generated' },
        { title: `Plaats vacature op LinkedIn en job boards`, enabled: true, source: 'generated' },
        { title: `Interview kandidaten`, enabled: true, source: 'generated' },
        { title: `Check referenties en skills`, enabled: true, source: 'generated' }
      );
    } else if (decision.resolution === 'delegate') {
      actions.push(
        { title: `Identificeer teamlid voor ${domainLabel}`, enabled: true, source: 'generated' },
        { title: `Plan overdracht gesprek`, enabled: true, source: 'generated' },
        { title: `Creëer SOP/handleiding`, enabled: true, source: 'generated' }
      );
    }

    // Set dates (spread over goal period)
    const today = new Date();
    const startDate = today.toISOString().split('T')[0];
    const endDate = new Date(today);
    endDate.setMonth(endDate.getMonth() + 3); // 3 months per milestone by default

    // Set priority based on resolution
    let priority: 'high' | 'medium' | 'low' = 'medium';
    if (decision.resolution === 'hire' || decision.resolution === 'outsource') {
      priority = 'high';
    } else if (decision.resolution === 'learn') {
      priority = 'medium';
    } else {
      priority = 'low';
    }

    milestones.push({
      domain,
      domainLabel,
      title,
      decision,
      actions,
      startDate,
      endDate: endDate.toISOString().split('T')[0],
      priority,
    });
  }

  return milestones;
}

function mapDomainToCategory(domain: string): 'richting_keuzes' | 'zichtbaarheid_markt' | 'vaardigheden_kennis' | 'systemen_structuur' | 'doorbraak_uitvoering' | 'opschaling_hefboom' {
  switch (domain) {
    case 'strategy':
      return 'richting_keuzes';
    case 'marketing':
      return 'zichtbaarheid_markt';
    case 'sales':
      return 'doorbraak_uitvoering';
    case 'product':
      return 'vaardigheden_kennis';
    case 'operations':
    case 'finance':
      return 'systemen_structuur';
    case 'team':
      return 'opschaling_hefboom';
    default:
      return 'systemen_structuur';
  }
}

export default PostAssessmentMilestonesReview;
